// Email service utility for Urban Zaikha contact form
// Using EmailJS for frontend email sending
import emailjs from '@emailjs/browser';

export interface EmailData {
  customerName?: string;
  phoneNumber?: string;
  message: string;
  timestamp?: string;
}

// EmailJS Configuration
const EMAILJS_CONFIG = {
  serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_urbanzaikha',
  templateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_contact',
  publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YOUR_EMAILJS_PUBLIC_KEY'
};

export const sendContactEmail = async (emailData: EmailData): Promise<boolean> => {
  try {
    // Email template parameters for EmailJS
    const templateParams = {
      from_name: emailData.customerName || 'Website Visitor',
      customer_name: emailData.customerName || 'Anonymous',
      phone_number: emailData.phoneNumber || 'Not provided',
      message: emailData.message,
      to_email: 'contact@urbanzaikha.com',
      timestamp: emailData.timestamp || new Date().toLocaleString(),
      subject: 'New Message from Urban Zaikha Website',
      reply_to: 'contact@urbanzaikha.com'
    };

    console.log('📧 Sending email via EmailJS...');
    console.log('📧 Configuration:', {
      serviceId: EMAILJS_CONFIG.serviceId,
      templateId: EMAILJS_CONFIG.templateId,
      publicKey: EMAILJS_CONFIG.publicKey?.substring(0, 8) + '...' // Hide most of the key for security
    });
    console.log('📝 Template parameters:', templateParams);

    // Check if EmailJS is properly configured
    const isConfigured = EMAILJS_CONFIG.publicKey !== 'YOUR_EMAILJS_PUBLIC_KEY' && 
                        EMAILJS_CONFIG.serviceId && 
                        EMAILJS_CONFIG.templateId;

    if (!isConfigured) {
      console.warn('⚠️ EmailJS not configured - check your environment variables');
      console.log('📝 Message received:', {
        from: emailData.customerName || 'Website Visitor',
        phone: emailData.phoneNumber || 'Not provided',
        message: emailData.message,
        timestamp: templateParams.timestamp
      });
      
      // Simulate successful email sending for development
      await new Promise(resolve => setTimeout(resolve, 1500));
      return true;
    }

    // Send email using EmailJS
    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );

    console.log('✅ Email sent successfully via EmailJS:', response);
    return true;

  } catch (error) {
    console.error('❌ Failed to send email:', error);
    
    // More detailed error logging
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error details:', error);
    }
    
    return false;
  }
};

// Alternative: Simple mailto fallback (opens user's email client)
export const openEmailClient = (emailData: EmailData): void => {
  const subject = encodeURIComponent('Message from Urban Zaikha Website');
  const body = encodeURIComponent(`
Name: ${emailData.customerName || 'Not provided'}
Phone: ${emailData.phoneNumber || 'Not provided'}
Message: ${emailData.message}
Sent: ${new Date().toLocaleString()}
  `);
  
  const mailtoLink = `mailto:contact@urbanzaikha.com?subject=${subject}&body=${body}`;
  window.open(mailtoLink);
};
